import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useState, type KeyboardEvent, type SyntheticEvent } from "react";

import { ALFRED_DESCRIPTION_MAX, ALFRED_TITLE_MAX } from "../lib/limits.ts";

import { displayNameOf } from "../app/identity.ts";
import { useSession } from "../app/session.tsx";
import { useReturnFocus } from "../work/digitacion/return-focus.ts";
import {
  createTicket,
  currentPage,
  ALFRED_TEXTS as TEXTS,
  type AlfredCcPerson,
} from "./alfred.ts";
import { AttachmentPicker } from "./AttachmentPicker.tsx";
import { CcPicker } from "./CcPicker.tsx";
import { useAttachedSave } from "./use-attachments.ts";

const EMPTY = { title: "", description: "" };

/**
 * «Cuéntale a Alfred lo que necesitas»: quién (de la sesión, no se
 * escribe), un título, una descripción y los adjuntos que haga falta,
 * también pegados con Ctrl+V. Se abre por estado --el botón de la cabecera,
 * el atajo, el menú de comandos--, así que devuelve el foco con
 * `useReturnFocus`.
 *
 * Lo escrito vive aquí y no en el contenido del diálogo, que se desmonta al
 * cerrar: si el envío falla, o se cancela, nada se pierde. Solo se vacía al
 * guardarse.
 *
 * Primero se crea el pedido y después se suben los adjuntos. Si alguno no
 * sube, el pedido ya está: lo escrito se queda a la vista, sin poder
 * cambiarse, y «Reintentar los adjuntos» sube solo lo que falta.
 */
export function AlfredDialog({
  open,
  onOpenChange,
  onSent,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSent: () => void;
}) {
  const { me } = useSession();
  const [draft, setDraft] = useState(EMPTY);
  const [checked, setChecked] = useState(false);
  const [cc, setCc] = useState<readonly AlfredCcPerson[]>([]);
  const returnFocus = useReturnFocus(open);
  const send = useAttachedSave({
    save: async (input: Parameters<typeof createTicket>[0]) => ({
      ticketId: (await createTicket(input)).id,
    }),
    onDone: () => {
      setDraft(EMPTY);
      setChecked(false);
      setCc([]);
      onOpenChange(false);
      onSent();
    },
  });
  const { files, saved } = send;

  const title = draft.title.trim();
  const description = draft.description.trim();
  const missingTitle = checked && title === "";
  const missingDescription = checked && description === "";

  const submit = (event?: SyntheticEvent) => {
    event?.preventDefault();
    if (send.pending) return;
    setChecked(true);
    if (title === "" || description === "") return;
    send.submit({
      title,
      description,
      page: currentPage(),
      ...(cc.length === 0
        ? {}
        : { ccUserIds: cc.map((person) => person.userId) }),
    });
  };

  /* Cerrar con el pedido ya creado es terminar: está guardado, aunque le
     falte algún adjunto. Sin crear, lo escrito y lo adjuntado se quedan. */
  const close = () => {
    if (send.pending) return;
    if (saved) {
      setDraft(EMPTY);
      setChecked(false);
      setCc([]);
      send.reset();
      onOpenChange(false);
      onSent();
      return;
    }
    send.dismiss();
    onOpenChange(false);
  };

  /* Ctrl+Enter (⌘↩ en Mac) envía desde cualquier campo. Se marca como
     atendido: en Digitación, la pantalla no lo toma por «Guardar». */
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
        else onOpenChange(next);
      }}
    >
      <DialogContent onCloseAutoFocus={returnFocus}>
        <form
          noValidate
          onSubmit={submit}
          onKeyDown={onKeyDown}
          onPaste={files.onPaste}
          className="grid gap-4"
        >
          <DialogHeader>
            <DialogTitle>{TEXTS.title}</DialogTitle>
            <DialogDescription>{TEXTS.description}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="alfred-quien">{TEXTS.who}</Label>
            <Input
              id="alfred-quien"
              readOnly
              tabIndex={-1}
              value={me === null ? "" : displayNameOf(me)}
              className="bg-muted"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="alfred-titulo">{TEXTS.subject}</Label>
            <Input
              id="alfred-titulo"
              autoFocus
              required
              readOnly={saved}
              maxLength={ALFRED_TITLE_MAX}
              value={draft.title}
              aria-invalid={missingTitle}
              aria-describedby={
                missingTitle ? "alfred-titulo-error" : undefined
              }
              onChange={(event) => {
                setDraft((value) => ({ ...value, title: event.target.value }));
              }}
            />
            {missingTitle && (
              <p id="alfred-titulo-error" className="text-sm text-destructive">
                {TEXTS.required(TEXTS.subject)}
              </p>
            )}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="alfred-descripcion">{TEXTS.body}</Label>
            <Textarea
              id="alfred-descripcion"
              required
              readOnly={saved}
              rows={5}
              maxLength={ALFRED_DESCRIPTION_MAX}
              value={draft.description}
              aria-invalid={missingDescription}
              aria-describedby={
                missingDescription ? "alfred-descripcion-error" : undefined
              }
              className="max-h-[40dvh]"
              onChange={(event) => {
                setDraft((value) => ({
                  ...value,
                  description: event.target.value,
                }));
              }}
            />
            {missingDescription && (
              <p
                id="alfred-descripcion-error"
                className="text-sm text-destructive"
              >
                {TEXTS.required(TEXTS.body)}
              </p>
            )}
          </div>
          <CcPicker value={cc} onChange={setCc} disabled={saved} />
          <div className="grid gap-1.5">
            <span className="text-sm font-medium">{TEXTS.attachments}</span>
            <AttachmentPicker files={files} disabled={send.pending} />
          </div>
          {send.error !== null && (
            <p role="alert" className="text-sm text-destructive">
              {send.error}
            </p>
          )}
          {saved && files.failed > 0 && !send.pending && (
            <p role="alert" className="text-sm text-destructive">
              {TEXTS.savedButUploads(files.failed)}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              {saved ? TEXTS.close : TEXTS.cancel}
            </Button>
            <Button
              type="submit"
              aria-disabled={send.pending}
              aria-keyshortcuts="Control+Enter Meta+Enter"
            >
              {send.pending
                ? TEXTS.sending
                : !saved
                  ? TEXTS.send
                  : files.unsent > 0
                    ? TEXTS.retryUploads
                    : TEXTS.done}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
